import Vue from 'vue'
import { Component, Watch } from 'vue-property-decorator'

import { ApolloResponse } from '@/interfaces/apollo'
import { Breadcrumb } from '@/interfaces/breadcrumbs'
import { TicketPreview, Ticket } from '@/modules/Ticket/interfaces/ticket'
import { showErrors } from '@/utils/snack'
import { tablePerPageOptions } from '@/utils/tablePerPageOptions'
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
import AppSearch from '@/components/AppSearch.vue'
import AppTableDate from '@/components/AppTableDate.vue'
import { getMyTickets, getAllTickets } from '@/modules/Ticket/gql/ticket.gql'
@Component({
  components: {
    AppBreadcrumbs,
    AppSearch,
    AppTableDate
  }
})
export default class TicketList extends Vue {
  breadcrumbs: Breadcrumb[] = [
    {
      text: 'Ticket',
      to: { name: 'TicketDashborad', params: {} }
    }
  ]
  searchVal = ''

  windowSize = {
    w: 0,
    h: 0,
    cW: 0
  }

  allTickets: TicketPreview[] = []

  orderBy: {
    [key: string]: string
  } = {
    name: 'NAME',
    expectedTime: 'EXPECTED_TIME',
    status: 'STATUS',
    createdAt: 'CREATED_AT',
    closed: 'CLOSED'
  }

  tableColumn = [
    { field: '', title: '', slots: { default: 'priority_default' }, fixed: 'left', width: 28 },
    { field: 'name', title: 'Ticket Name', slots: { default: 'name_default' }, resizable: true },
    { field: 'founder', title: 'Founder', resizable: true },
    { title: 'Expected time',
      field: 'expectedTime',
      slots: { default: 'expected_time' },
      resizable: true,
      width: 150
    },
    { field: 'assignees', title: 'Assignees', slots: { default: 'assignees_default' }, resizable: true },
    { field: 'supervisors', title: 'Supervisors', slots: { default: 'supervisors_default' }, resizable: true },
    { field: 'status', title: 'Status', slots: { default: 'status_default' }, resizable: true, width: 130 },
    { title: 'Creation time',
      field: 'createdAt',
      slots: { default: 'created_at' },
      width: 150,
      resizable: true
    },
    { field: 'closed', title: 'Open/Close', slots: { default: 'closed_default' }, fixed: 'right', width: 120 }
  ]

  tableOptions = {
    loading: false,
    tablePage: tablePerPageOptions(),
    sortBy: 'name',
    sortDesc: true
  }

  created () {
    const { type, search } = this.$route.params
    const breadcrumbText = type === 'openning' ? 'Openning' : ''
    this.breadcrumbs.push({
      text: `All ${breadcrumbText} Tickets`
    })
    this.searchVal = search
  }

  mounted () {
    this.onResize()
    this.getTickets()
  }

  getTickets () {
    const { type } = this.$route.params
    if (type === 'openning') {
      this.getMyTickets()
    } else {
      this.getAllTickets()
    }
  }

  onResize () {
    const container = this.$refs.container as Vue & { clientWidth: number }
    this.windowSize = { w: window.innerWidth, h: window.innerHeight, cW: container.clientWidth }
  }

  routeTo (name: string, params: any) {
    this.$router.push({
      name,
      params
    })
  }

  routeToDetail (item: any) {
    this.routeTo('TicketDetail', {
      id: item.id
    })
  }

  async getMyTickets () {
    const { sortBy, sortDesc, tablePage } = this.tableOptions
    const { currentPage, pageSize } = tablePage
    const searchVal = this.searchVal ? this.searchVal : null
    const orderBy = this.orderBy[sortBy] || 'CREATED_AT'
    this.tableOptions.loading = true
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getMyTickets,
        variables: {
          filter: {
            name: searchVal
          },
          order: {
            orderBy: orderBy,
            asc: !sortDesc
          },
          paging: {
            offset: (currentPage - 1) * pageSize,
            limit: pageSize
          }
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      const myTopTickets = result.data.getMyTickets.tickets
      this.allTickets = myTopTickets.map((ticket: Ticket) => {
        return this.$R.merge(ticket, {
          founder: ticket.users.founder.username,
          supervisors: this.$R.map((supervisor: any) => {
            return supervisor.username
          }, ticket.users.supervisors),
          assignees: this.$R.map((assignee: any) => {
            return assignee.username
          }, ticket.users.assignees)
        })
      })
      this.tableOptions.loading = false
      this.tableOptions.tablePage.total = result.data.getMyTickets.paging.totalCount
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async getAllTickets () {
    const { sortBy, sortDesc, tablePage } = this.tableOptions
    const { currentPage, pageSize } = tablePage
    const searchVal = this.searchVal ? this.searchVal : null
    const orderBy = this.orderBy[sortBy] || 'CREATED_AT'
    this.tableOptions.loading = true
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getAllTickets,
        variables: {
          filter: {
            name: searchVal
          },
          order: {
            orderBy: orderBy,
            asc: !sortDesc
          },
          paging: {
            offset: (currentPage - 1) * pageSize,
            limit: pageSize
          }
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      const allTopTickets = result.data.getAllTickets.tickets
      this.allTickets = allTopTickets.map((ticket: Ticket) => {
        return this.$R.merge(ticket, {
          founder: ticket.users.founder.username,
          supervisors: this.$R.map((supervisor: any) => {
            return supervisor.username
          }, ticket.users.supervisors),
          assignees: this.$R.map((assignee: any) => {
            return assignee.username
          }, ticket.users.assignees)
        })
      })
      this.tableOptions.loading = false
      this.tableOptions.tablePage.total = result.data.getAllTickets.paging.totalCount
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  sortTable (column: any) {
    this.tableOptions.sortBy = column.property
    this.tableOptions.sortDesc = column.order === 'desc'
    this.getTickets()
  }

  handlePageChange (pageData: any) {
    this.tableOptions.tablePage.currentPage = pageData.currentPage
    this.tableOptions.tablePage.pageSize = pageData.pageSize
    this.getTickets()
  }
}
